import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { getSurveys, getCategories } from '@/lib/api';
import { RevealOnScroll } from './Motion';

const EASE = [0.16, 1, 0.3, 1];

function SurveyCard({ survey, index }) {
    return (
        <motion.a
            href={survey.url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.8, ease: EASE, delay: index * 0.06 }}
            className="ink-card group block border border-ink bg-sand-100 hover:bg-sand-200"
            data-testid={`survey-card-${survey.id}`}
        >
            {/* Image */}
            <div className="relative aspect-[4/3] overflow-hidden border-b border-ink">
                {survey.image_url ? (
                    <img
                        src={survey.image_url}
                        alt={survey.title}
                        className="ink-card__image w-full h-full object-cover grayscale group-hover:grayscale-0"
                    />
                ) : (
                    <div className="ink-card__image w-full h-full bg-gradient-to-br from-ink to-sand-300 flex items-center justify-center">
                        <span className="font-display font-black text-6xl text-sand-100">
                            {survey.provider?.[0] ?? 'S'}
                        </span>
                    </div>
                )}
                <div className="absolute top-4 start-4 bg-sand-100 border border-ink px-3 py-1 text-[10px] uppercase tracking-widest">
                    {survey.category}
                </div>
                <div className="absolute top-4 end-4 bg-ink text-sand-100 px-3 py-1 text-[10px] uppercase tracking-widest">
                    {survey.provider}
                </div>
            </div>

            <div className="p-6 md:p-8">
                <h3 className="font-display font-black text-xl md:text-2xl leading-[1.2] tracking-tight">
                    {survey.title}
                </h3>
                <p className="text-ink-600 mt-3 text-sm leading-relaxed line-clamp-3">
                    {survey.description}
                </p>

                <div className="mt-6 pt-5 border-t border-ink/20 flex items-end justify-between gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400">
                            المكافأة
                        </div>
                        <div className="font-display font-black text-xl md:text-2xl text-emerald-signal">
                            {survey.reward}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400">
                            المدة
                        </div>
                        <div className="font-display font-medium text-base">
                            {survey.duration}
                        </div>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.25em] hover-arrow">
                        ابدأ
                        <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                            <path d="M13 7L1 7M1 7L7 1M1 7L7 13" stroke="currentColor" strokeWidth="1.5" />
                        </svg>
                    </div>
                </div>
            </div>
        </motion.a>
    );
}

export default function SurveyGrid() {
    const [surveys, setSurveys] = useState([]);
    const [categories, setCategories] = useState(['الكل']);
    const [active, setActive] = useState('الكل');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [s, c] = await Promise.all([getSurveys(), getCategories()]);
                setSurveys(s);
                setCategories(c);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filtered = useMemo(() => {
        if (active === 'الكل') return surveys;
        return surveys.filter((s) => s.category === active);
    }, [surveys, active]);

    return (
        <section
            id="directory"
            className="relative py-32 border-t border-ink/20"
            data-testid="directory-section"
        >
            <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                <RevealOnScroll>
                    <div className="flex items-baseline justify-between flex-wrap gap-6 mb-14">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.3em] mb-4 text-ink-600">
                                — الدليل الكامل
                            </div>
                            <h2 className="font-display font-black text-4xl md:text-6xl leading-[1.05] tracking-tight">
                                أفضل الاستطلاعات المتاحة<br />
                                <span className="italic font-light">في مكان واحد.</span>
                            </h2>
                        </div>
                        <div className="text-ink-600 max-w-sm text-sm leading-relaxed">
                            انقر على أي بطاقة لتُنقل مباشرة إلى المنصّة الأصلية.
                            جميع الروابط مُراجَعة ومُحدَّثة.
                        </div>
                    </div>
                </RevealOnScroll>

                {/* Filters */}
                <RevealOnScroll delay={0.1}>
                    <div className="flex flex-wrap gap-x-8 gap-y-3 mb-12 border-y border-ink/20 py-4">
                        {categories.map((c) => (
                            <button
                                key={c}
                                onClick={() => setActive(c)}
                                data-testid={`filter-${c}`}
                                className={`text-sm font-medium transition-all relative pb-1 ${
                                    active === c
                                        ? 'text-ink'
                                        : 'text-ink-400 hover:text-ink'
                                }`}
                            >
                                {c}
                                {active === c && (
                                    <motion.span
                                        layoutId="filter-underline"
                                        className="absolute -bottom-1 inset-x-0 h-px bg-ink"
                                    />
                                )}
                            </button>
                        ))}
                    </div>
                </RevealOnScroll>

                {loading ? (
                    <div
                        className="py-20 text-center text-ink-400"
                        data-testid="directory-loading"
                    >
                        جاري التحميل…
                    </div>
                ) : filtered.length === 0 ? (
                    <div
                        className="py-20 text-center text-ink-400 border border-dashed border-ink/30"
                        data-testid="directory-empty"
                    >
                        لا توجد استطلاعات في هذه الفئة بعد. أضف واحدًا من لوحة الإدارة.
                    </div>
                ) : (
                    <AnimatePresence mode="popLayout">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {filtered.map((s, i) => (
                                <SurveyCard key={s.id} survey={s} index={i} />
                            ))}
                        </div>
                    </AnimatePresence>
                )}
            </div>
        </section>
    );
}
